<template>
  <article v-if="labelIsValid && companyIsValid && isExperimentEnabled" class="climate-pledge-info">
    <div class="climate-pledge-info__content">
      <EHeading as="h3" class="climate-pledge-info__heading">
        {{ $t('web.green.climatepledge.infobox.heading') }}
      </EHeading>
      <p class="climate-pledge-info__subheading">
        {{ getCompanyNameLabel }}
      </p>

      <div class="climate-pledge-info__ratings">
        <EClimateRating
          v-for="rating in ratings"
          :key="rating.score"
          :rating="rating"
          :is-active="rating.score === ecosiaLabel"
          :on-click="showModal"
        />
      </div>
      <div class="climate-pledge-info__toggle">
        <div class="climate-pledge-info__rating-label">
          {{ $t('web.green.climatepledge.infobox.rated.text') }}: <span>{{ ratingType }}</span>
        </div>
        <transition name="fade">
          <div class="climate-pledge-info__toggle-button">
            <EButton
              :icon="toggleIcon"
              variant="bare"
              icon-position="end"
              size="s"
              data-test-id="toggleDetailsButton"
              @click="toggleDetails"
            >
              {{ showDetails ?
                $t('web.green.climatepledge.infobox.hidedetails') :
                $t('web.green.climatepledge.infobox.showdetails')
              }}
            </EButton>
          </div>
        </transition>
      </div>
      <transition name="toggle">
        <div v-if="showDetails" class="climate-pledge-info__details">
          <div
            v-if="!hideCurrentPledge"
            class="climate-pledge-info__details-section"
            data-test-id="climate-pledge-info-current-text"
          >
            <div class="climate-pledge-info__details-icon-wrapper">
              <EIcon
                icon="check-circle"
                size="m"
                class="climate-pledge-info__details-icon"
              />
            </div>
            <div>
              {{ $t(`web.green.climatepledge.${getCompanyInfo.name}.current`) }}
            </div>
          </div>
          <div
            v-if="`$t('web.green.climatepledge.${getCompanyInfo.name}.missing')`"
            class="climate-pledge-info__details-section"
            data-test-id="climate-pledge-info-missing-text"
          >
            <div class="climate-pledge-info__details-icon-wrapper">
              <EIcon
                icon="exclamation-circle"
                size="m"
                class="climate-pledge-info__details-icon"
              />
            </div>
            <div>
              {{ $t(`web.green.climatepledge.${getCompanyInfo.name}.missing`) }}
            </div>
          </div>
          <EButton
            v-analytics.click="trackSeeMeaningClick"
            :variant="whatItmeansBtnVariantExperiment"
            size="s"
            @click="showModal"
          >
            {{ $t('common.green.climatepledge.seewhatitmeans') }}
          </EButton>
          <EButton
            v-if="isTakeActionEnabled && isAmazon"
            v-analytics.click="trackUserAction"
            variant="solid-green"
            size="s"
            class="take-action-button"
            @click="showActionModal"
          >
            {{ actionText }}
          </EButton>
        </div>
      </transition>
    </div>
    <div
      v-if="!isDesktopUp"
      class="climate-pledge-info__footer-wave"
    >
      <EWaveMainline aria-hidden="true" />
    </div>
    <div
      v-if="isDesktopUp"
      class="climate-pledge-info__footer-wave"
    >
      <EWaveSidebar aria-hidden="true" />
    </div>
    <footer>
      <section class="climate-pledge-info__footer-content-container">
        <div class="climate-pledge-info__footer-source">
          <div v-if="getCompanyInfo.sources" class="climate-pledge-info__footer-source-label">
            {{
              getCompanyInfo.sources.length > 1 ?
                $t('web.green.climatepledge.sourcesText') :
                $t('web.green.climatepledge.sourceText')
            }}:
          </div>
          <li
            v-for="(source) in sources"
            :key="source.title"
            class="climate-pledge-info__footer-source-link"
          >
            <ELink
              v-analytics.click="trackSourceClick(source.url)"
              :href="source.url"
              target="_blank"
              color="bare"
            >
              {{ source.title }}
            </ELink>
          </li>
        </div>
      </section>
      <section
        class="climate-pledge-info__footer-content-container climate-pledge-info__footer--padding"
      >
        <div class="climate-pledge-info__footer-content-left">
          Powered by Ecosia
        </div>
        <div class="climate-pledge-info__footer-content-right">
          <ELink
            as="button"
            color="secondary"
            data-test-id="infobox-feedback-link"
            @click="onFeedbackLinkClick"
          >
            {{ $t('common.footer.feedback') }}
          </ELink>
        </div>
      </section>
    </footer>
    <EClimatePledgeModal
      v-if="labelIsValid && companyIsValid"
      :active="isModalVisible"
      :company-info="getCompanyInfo"
      :rating="ecosiaLabel"
      trigger-location="infobox"
      @close="hideModal"
      @cp-feedback-link-click="onFeedbackLinkClick"
      @show-cpr-action-modal="showActionModal"
    />
    <ETakeActionModal
      v-if="labelIsValid && companyIsValid && isAmazon && isTakeActionEnabled"
      :active="isActionModalVisible"
      @close="hideActionModal"
      @cp-feedback-link-click="onFeedbackLinkClick"
    />
    <EFeedbackModal
      v-if="labelIsValid && companyIsValid"
      v-bind="feedbackModal"
      :active="!isModalVisible && isCpFeedbackVisible"
      @close="hideFeedback"
    />
  </article>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import EHeading from '~~/common/vue/components/heading';
import EButton from '~~/common/vue/components/button';
import ELink from '~~/common/vue/components/link';
import EIcon from '~~/common/vue/components/icon';
import EFeedbackModal from '~~/common/vue/components/feedback-modal';
import EClimatePledgeModal from '~~/search/common/vue/components/climate-pledge/climate-pledge-modal';
import ETakeActionModal from '~~/search/common/vue/components/climate-pledge/take-action-modal';
import screen from '~~/common/vue/mixins/screen';
import EClimateRating from './climate-rating';
import {
  climatePledgeInfoboxDisplay,
  climatePledgeInfoboxShowDetailsClick,
  climatePledgeInfoboxHideDetailsClick,
  climatePledgeInfoboxSeeMeaningClick,
  climatePledgeInfoboxSourcesClick,
  climatePledgeUserActionClick,
} from '~~/search/common/js/client/analytics/climate-pledge-ratings';
import companyMetaData from '~~/search/common/vue/components/climate-pledge/companyMetaData';
import { greenFeatures, cprInfoBoxState, cprInfoBoxMeaningBtnAppearance } from '~~/search/common/js/client/feature-flags';

const capitalize = (str) => (str ? str[0].toUpperCase() + str.slice(1) : '');

export default {
  name: 'EClimatePledgeInfo',
  components: {
    EHeading,
    EButton,
    EIcon,
    ELink,
    EFeedbackModal,
    EClimateRating,
    EClimatePledgeModal,
    ETakeActionModal,
    EWaveMainline: async () => import(
      '~~/search/common/vue/assets/img/waves-footer-mainline.svg'
    ),
    EWaveSidebar: async () => import(
      '~~/search/common/vue/assets/img/waves-footer-sidebar.svg'
    ),
  },
  mixins: [screen],
  props: {
    ecosiaLabel: {
      type: String,
      required: true,
    },
    organization: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showDetails: false,
      isModalVisible: false,
      isActionModalVisible: false,
      isCpFeedbackVisible: false,
    };
  },
  computed: {
    ...mapGetters('unleash', ['getFeatureToggle']),
    ...mapState('user', ['bingMarketCode']),
    isExperimentEnabled() {
      return this.getFeatureToggle(greenFeatures.flag)?.value === greenFeatures.enabled;
    },
    labelIsValid() {
      return this.ratings.map(({ score }) => score).includes(this.ecosiaLabel);
    },
    companyIsValid() {
      return companyMetaData[this.organization.parentName] ||
        companyMetaData[this.organization.name];
    },
    hideCurrentPledge() {
      return this.getCompanyInfo.hideCurrentPledge;
    },
    getCompanyNameLabel() {
      return this.organization.parentName ?
        `${capitalize(this.organization.parentName)} (${this.$t('web.green.climatepledge.infobox.owns.text')} ${capitalize(this.organization.name)})` :
        capitalize(this.organization.name);
    },
    getCompanyInfo() {
      return this.organization.parentName ?
        {
          ...companyMetaData[this.organization.parentName],
          name: this.organization.parentName,
        } :
        { ...companyMetaData[this.organization.name], name: this.organization.name };
    },
    ratings() {
      return [
        {
          score: 'CLIMATE_PLEDGE_RATING_A',
          type: this.$t('web.green.climatepledge.type.leading'),
          text: this.$t('web.green.climatepledge.score.a'),
        },
        {
          score: 'CLIMATE_PLEDGE_RATING_B',
          type: this.$t('web.green.climatepledge.type.good'),
          text: this.$t('web.green.climatepledge.score.b'),
        },
        {
          score: 'CLIMATE_PLEDGE_RATING_C',
          type: this.$t('web.green.climatepledge.type.modest'),
          text: this.$t('web.green.climatepledge.score.c'),
        },
        {
          score: 'CLIMATE_PLEDGE_RATING_D',
          type: this.$t('web.green.climatepledge.type.weak'),
          text: this.$t('web.green.climatepledge.score.d'),
        },
        {
          score: 'CLIMATE_PLEDGE_RATING_F',
          type: this.$t('web.green.climatepledge.type.failing'),
          text: this.$t('web.green.climatepledge.score.f'),
        },
      ];
    },
    ratingType() {
      const rating = this.ratings.find((currRating) => currRating.score === this.ecosiaLabel);
      return rating?.type;
    },
    toggleIcon() {
      const defaultIcon = this.showDetails ? 'chevron-mini-up' : 'chevron-mini-down';
      return defaultIcon;
    },
    feedbackModal() {
      return {
        errorMessage: this.$t('common.header.feedback.error'),
        options: [
          { label: this.$t('common.header.feedback.bullet1'), value: 'helpful' },
          { label: this.$t('common.header.feedback.bullet2'), value: 'missing' },
          { label: this.$t('common.header.feedback.bullet3'), value: 'wrong' },
          { label: this.$t('common.header.feedback.bullet4'), value: 'not_helpful' },
        ],
        submitText: this.$t('common.header.feedback.send'),
        successMessage: this.$t('common.header.feedback.body2'),
        successTitle: this.$t('common.header.feedback.title2'),
        textareaPlaceholder: this.$t('common.header.feedback.tellmore'),
        title: this.$t('common.header.feedback.login'),
        ticketSubject: 'Feedback - Green - Climate Pledge Rating Infobox',
        url: null,
        widgetType: 'climate_pledge_entity',
        query: this.$route.query.q?.toString(),
        bingMarketCode: this.bingMarketCode,
      };
    },
    sources() {
      const sources = [];
      for (let index = 0; index < this.getCompanyInfo.sources.length; index += 1) {
        const searchTitle = `title${index + 1}`;
        const source = this.getCompanyInfo.sources.find((item) => !!item[searchTitle]);
        sources.push({
          title: source[searchTitle],
          url: source.url,
        });
      }
      return sources;
    },
    trackSeeMeaningClick() {
      return climatePledgeInfoboxSeeMeaningClick;
    },
    isDeskTopAbove() {
      return this.isDesktopUp;
    },
    infoBoxDisplayEvent() {
      return new Set();
    },
    isTakeActionEnabled() {
      return this.getFeatureToggle('cpr_action_button_text')?.value === 'share' ||
            this.getFeatureToggle('cpr_action_button_text')?.value === 'take_action';
    },
    actionText() {
      return this.getFeatureToggle('cpr_action_button_text')?.value === 'share' ? 'Share rating' : 'Take action';
    },
    isAmazon() {
      const orgName = this.organization.parentName || this.organization.name;
      return orgName === 'amazon';
    },
    stateExperimentVariant() {
      return this.getFeatureToggle(cprInfoBoxState.flag)?.value;
    },
    whatItmeansBtnVariantExperiment() {
      const variants = {
        [cprInfoBoxMeaningBtnAppearance.variants.outline]: 'outline',
        [cprInfoBoxMeaningBtnAppearance.variants['no-outline']]: 'solid-white',
      };
      const variant = this.getFeatureToggle(cprInfoBoxMeaningBtnAppearance.flag)?.value;
      return variants[variant];
    },
    trackUserAction() {
      const label = this.actionText?.split(' ')?.join('_');
      return climatePledgeUserActionClick(label, 'infobox');
    },
  },
  watch: {
    isDeskTopAbove(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        if (this.isExperimentEnabled) {
          this.trackDisplay();
        }
      }
    },
  },
  mounted() {
    if (this.labelIsValid && this.companyIsValid &&
        this.isExperimentEnabled && this.isDeskTopAbove) {
      this.trackDisplay();
      // if infobox toggle experiment variant is "expanded", set infobox as such
      // else leave collapsed as default.
      if (this.stateExperimentVariant === cprInfoBoxState.variants.expanded) {
        this.showDetails = true;
      }
      // if &cpr is passed we force the climate pledge modal to automatically open
      const hasCpr = 'cpr' in this.$route.query;
      if (hasCpr && this.isAmazon) {
        this.isModalVisible = true;
      }
    }
  },
  methods: {
    toggleDetails() {
      this.showDetails = !this.showDetails;
      if (this.showDetails) {
        this.$track(climatePledgeInfoboxShowDetailsClick);
      } else {
        this.$track(climatePledgeInfoboxHideDetailsClick);
      }
    },
    showModal() {
      this.isModalVisible = true;
    },
    showActionModal() {
      this.isModalVisible = false;
      this.isActionModalVisible = true;
    },
    hideModal() {
      this.isModalVisible = false;
    },
    hideActionModal() {
      this.isActionModalVisible = false;
    },
    showFeedback() {
      this.isCpFeedbackVisible = true;
    },
    hideFeedback() {
      this.isCpFeedbackVisible = false;
    },
    trackSourceClick(url) {
      return climatePledgeInfoboxSourcesClick(url);
    },
    onFeedbackLinkClick() {
      this.hideModal();
      this.hideActionModal();
      this.showFeedback();
    },
    trackDisplay() {
      const key = [
        this.organization.parentName,
        this.organization.name,
        this.ecosiaLabel,
      ].join();
      const has = this.infoBoxDisplayEvent.has(key);
      if (!has) {
        this.$track(climatePledgeInfoboxDisplay);
        this.infoBoxDisplayEvent.add(key);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.climate-pledge-info {
  background-color: var(--color-background-quaternary);

  @include desktop {
    background-color: var(--color-background-secondary);
    border-radius: $space-s $space-s 0 0;
  }
}

.climate-pledge-info__content {
  padding: $space-m;
}

.climate-pledge-info__heading {
  font-size: $font-1l;
  font-weight: $font-weight-m;
  color: var(--color-text-primary);
}

.climate-pledge-info__subheading {
  font-size: $font-m;
  color: var(--color-text-secondary);
  margin-top: 0;
}

.climate-pledge-info__ratings {
  display: flex;
  margin-bottom: $space-s;
}

.climate-pledge-info__rating-label {
  color: var(--color-text-primary);

  span {
    font-weight: $font-weight-1l;
  }
}

.climate-pledge-info__toggle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: $space-m;

  @media only screen and (min-width: $screen-desktop) and (max-width: $screen-desktop-l) {
    flex-wrap: wrap;
  }
}

.climate-pledge-info__toggle-button {
  width: 60%;
  text-align: right;

  @media only screen and (min-width: $screen-desktop) and (max-width: $screen-desktop-l) {
    text-align: left;
    width: 100%;
    margin-left: -11px; // adjust button text
    margin-top: $space-2s;
  }
}

.climate-pledge-info__details {
  color: var(--color-text-secondary);
  max-height: 450px;
  overflow: hidden;
}

.climate-pledge-info__details-section {
  display: flex;
  margin-bottom: $space-s;
}

.climate-pledge-info__details-icon {
  color: $c-gray-50;
}

.climate-pledge-info__details-icon-wrapper {
  width: $space-2l;
  padding-right: $space-s;
}

.toggle-enter-active,
.toggle-leave-active {
  transition: max-height $timing-m;
}

.toggle-enter,
.toggle-leave-to {
  max-height: 0;
}

.climate-pledge-info__footer-wave {
  width: 100%;
  height: auto;
  color: var(--color-background-primary);
  overflow: hidden;
}

footer {
  background-color: var(--color-background-secondary);
  padding: $space-1s $space-l;
  display: flex;
  flex-direction: column;
  font-size: $font-1s;
  color: var(--color-link-secondary);

  @include desktop {
    padding: $space-1s $space-m;
    background-color: var(--color-button-background-secondary);
  }
}

.climate-pledge-info__footer-content-container {
  width: 100%;
  display: flex;
}

.climate-pledge-info__footer-source {
  width: 80%;
}

.climate-pledge-info__footer-content-left {
  width: 80%;
}

.climate-pledge-info__footer-source-label {
  display: inline-block;
}

.climate-pledge-info__footer--padding {
  padding: $space-1s  0 0 0;
}

.climate-pledge-info__footer-source-link {
  display: inline-block;
}

.take-action-button {
  margin-left: $space-s;
}
</style>
